import { useState, useEffect, useMemo } from 'react';
import { showAlert } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import AdminLayout from '../components/AdminLayout';
import {
  Plus, Trash2, Save, CalendarDays, CheckCircle2, Eye, BellRing,
} from 'lucide-react';

type Rule = { vatDays: number; ctMonths: number; vatThreshold: number; bookDay: number };
type StandardDate = { name: string; cat: string; date: string };
type Obligation = { n: string; cat: string; due: Date | null; freq: string; std?: boolean };
type StatusKey = 'overdue' | 'due' | 'upcoming' | 'scheduled';

const DEFAULT_RULES: Rule = { vatDays: 28, ctMonths: 9, vatThreshold: 375000, bookDay: 5 };
const DEFAULT_REMINDERS = [90, 30, 21, 14, 7, 6, 5, 4, 3, 2, 1];

/** Offered as one click each; anything else can still be typed in. */
const PRESET_REMINDERS = [90, 60, 30, 21, 14, 10, 7, 5, 3, 2, 1];

// Sample client dates — only used for the live preview (Phase 2 = real per-client data)
const SAMPLE = {
  vatReg: 'Yes', vatFreq: 'Quarterly', vatPeriodEnd: '2026-06-30',
  fyEnd: '2025-12-31', ctReg: 'No', ctDeadline: '2026-09-30',
  licenceExpiry: '2026-08-15', turnover: 420000,
};

const parse = (d: string): Date | null => d ? new Date(d + 'T00:00:00') : null;
const addDays = (d: Date, n: number): Date => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d: Date, n: number): Date => {
  const x = new Date(d); const day = x.getDate(); x.setDate(1);
  x.setMonth(x.getMonth() + n);
  x.setDate(Math.min(day, new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate()));
  return x;
};
const getToday = (): Date => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; };
const nextMonthDay = (n: number): Date => {
  const t = getToday();
  const day = Math.min(Math.max(1, n), 28); // clamp to avoid month-end overflow
  return new Date(t.getFullYear(), t.getMonth() + 1, day);
};
const fmt = (d: Date | null) =>
  d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const daysLeft = (d: Date | null): number | null =>
  d ? Math.round((d.getTime() - getToday().getTime()) / 86400000) : null;

/** "in 12 days" / "in 4 months" — the reading an admin actually wants from a date. */
const relative = (d: Date | null): string => {
  const n = daysLeft(d);
  if (n === null) return '';
  if (n < 0) return `${Math.abs(n)} days ago`;
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n < 45) return `in ${n} days`;
  return `in ${Math.round(n / 30)} months`;
};

const statusOf = (d: Date | null): [string, StatusKey] => {
  const n = daysLeft(d);
  if (n === null) return ['Scheduled', 'scheduled'];
  if (n < 0)      return ['Overdue',   'overdue'];
  if (n <= 30)    return ['Due soon',  'due'];
  if (n <= 90)    return ['Upcoming',  'upcoming'];
  return ['Scheduled', 'scheduled'];
};

const STATUS_STYLES: Record<StatusKey, { dot: string; pill: string }> = {
  overdue:   { dot: 'bg-red-500',   pill: 'bg-red-100 text-red-700' },
  due:       { dot: 'bg-amber-500', pill: 'bg-amber-100 text-amber-700' },
  upcoming:  { dot: 'bg-blue-500',  pill: 'bg-blue-100 text-blue-700' },
  scheduled: { dot: 'bg-green-500', pill: 'bg-green-100 text-green-700' },
};

function computeObligations(rules: Rule, standardDates: StandardDate[]): Obligation[] {
  const c = SAMPLE;
  const today = getToday();
  const L: Obligation[] = [];

  if (c.vatReg === 'Yes' && c.vatPeriodEnd) {
    const d = parse(c.vatPeriodEnd);
    L.push({ n: 'VAT return + payment', cat: 'VAT', due: d ? addDays(d, rules.vatDays) : null, freq: c.vatFreq });
  }
  if (c.ctReg === 'Yes' && c.fyEnd) {
    const d = parse(c.fyEnd);
    L.push({ n: 'Corporate Tax return + payment', cat: 'Corporate Tax', due: d ? addMonths(d, rules.ctMonths) : null, freq: 'Annual' });
  }
  if (c.ctReg === 'No' && c.ctDeadline) {
    L.push({ n: 'Corporate Tax registration (pending)', cat: 'Corporate Tax', due: parse(c.ctDeadline), freq: 'One-time' });
  }
  if (c.vatReg === 'No' && c.turnover >= rules.vatThreshold) {
    L.push({ n: 'VAT registration (threshold passed)', cat: 'VAT', due: addDays(today, 20), freq: 'Action' });
  }
  if (c.licenceExpiry) {
    L.push({ n: 'Trade licence renewal', cat: 'Licence', due: parse(c.licenceExpiry), freq: 'Annual' });
  }
  L.push({ n: 'Monthly bookkeeping close', cat: 'Bookkeeping', due: nextMonthDay(rules.bookDay), freq: 'Monthly' });
  L.push({ n: 'Document upload',           cat: 'Bookkeeping', due: nextMonthDay(rules.bookDay), freq: 'Monthly' });

  standardDates.forEach(o =>
    L.push({ n: o.name, cat: o.cat || 'Standard', due: parse(o.date), freq: 'Standard', std: true })
  );
  L.sort((a, b) => (a.due?.getTime() ?? 8e15) - (b.due?.getTime() ?? 8e15));
  return L;
}

/**
 * A number typed inside a sentence. The rules used to be a label / box / unit
 * grid, which made you assemble "28" + "days after period" into a meaning in
 * your head; written out as a sentence there is nothing to assemble.
 */
function NumIn({ value, min, max, width = 68, onChange }: {
  value: number; min: number; max: number; width?: number; onChange: (v: number) => void;
}) {
  return (
    <input
      type="number" value={value} min={min} max={max}
      onChange={e => onChange(Math.min(max, Math.max(min, +e.target.value || min)))}
      className="inp"
      style={{ width, display: 'inline-block', padding: '6px 8px', textAlign: 'center', fontWeight: 700 }}
    />
  );
}

function RuleRow({ children, example }: { children: React.ReactNode; example: string }) {
  return (
    <div className="py-3.5 border-b border-[var(--ax-line-2)] last:border-0 last:pb-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm text-[var(--ax-ink)]">{children}</div>
      <p className="hint mt-2">{example}</p>
    </div>
  );
}

export default function AdminComplianceCalendar() {
  const [rules, setRules]                 = useState<Rule>(DEFAULT_RULES);
  const [reminders, setReminders]         = useState<number[]>(DEFAULT_REMINDERS);
  const [standardDates, setStandardDates] = useState<StandardDate[]>([]);
  /** The last state that reached Firestore — what "unsaved changes" compares against. */
  const [saved, setSaved] = useState({ rules: DEFAULT_RULES, reminders: DEFAULT_REMINDERS, standardDates: [] as StandardDate[] });
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving]     = useState(false);
  const [savedAt, setSavedAt]   = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newCat, setNewCat]   = useState('');
  const [newDate, setNewDate] = useState('');
  const [customDay, setCustomDay] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'calendar', 'standard'));
        if (snap.exists()) {
          const data = snap.data();
          const r  = data.rules ? { ...DEFAULT_RULES, ...data.rules } : DEFAULT_RULES;
          const rm = Array.isArray(data.reminderDays)  ? data.reminderDays  : DEFAULT_REMINDERS;
          const sd = Array.isArray(data.standardDates) ? data.standardDates : [];
          setRules(r); setReminders(rm); setStandardDates(sd);
          setSaved({ rules: r, reminders: rm, standardDates: sd });
        }
      } catch (e: any) {
        console.error('Failed to load calendar/standard:', e);
        if (e?.code === 'permission-denied') {
          setLoadError('Firestore permission denied. The admin session is not authenticated with Firebase. Saved settings cannot be loaded or saved. Check Firestore security rules.');
        } else {
          setLoadError(`Failed to load saved settings (${e?.message ?? 'unknown error'}): showing defaults.`);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const obligations = useMemo(() => computeObligations(rules, standardDates), [rules, standardDates]);

  const dirty = useMemo(
    () => JSON.stringify({ rules, reminders, standardDates }) !== JSON.stringify(saved),
    [rules, reminders, standardDates, saved],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAdminConfig('calendar/standard', { rules, reminderDays: reminders, standardDates });
      setSaved({ rules, reminders, standardDates });
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'calendar/standard');
    } catch (e) {
      console.error('Save failed:', e);
      showAlert('Save failed. Check console.');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setRules(saved.rules); setReminders(saved.reminders); setStandardDates(saved.standardDates);
  };

  const addDate = () => {
    if (!newName.trim() || !newDate) return;
    setStandardDates(prev => [...prev, { name: newName.trim(), cat: newCat.trim() || 'Standard', date: newDate }]);
    setNewName(''); setNewCat(''); setNewDate('');
  };
  const removeDate = (i: number) => setStandardDates(prev => prev.filter((_, idx) => idx !== i));
  const updateRule = (key: keyof Rule, v: number) => setRules(prev => ({ ...prev, [key]: v }));

  // Reminders are a set, not a list: one click adds or drops a day, and the
  // order is derived rather than typed, so duplicates and 0s cannot happen.
  const toggleReminder = (d: number) =>
    setReminders(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort((a, b) => b - a));
  const addCustomReminder = () => {
    const d = Math.round(+customDay);
    if (!d || d < 1 || d > 365 || reminders.includes(d)) { setCustomDay(''); return; }
    setReminders(prev => [...prev, d].sort((a, b) => b - a));
    setCustomDay('');
  };
  const reminderChips = useMemo(
    () => Array.from(new Set([...PRESET_REMINDERS, ...reminders])).sort((a, b) => b - a),
    [reminders],
  );

  // Shown by date, saved in entry order — so the delete has to carry its index.
  const datesByDay = useMemo(
    () => standardDates.map((d, i) => ({ ...d, i })).sort((a, b) => a.date.localeCompare(b.date)),
    [standardDates],
  );

  const sampleVatDue = addDays(parse(SAMPLE.vatPeriodEnd)!, rules.vatDays);
  const sampleCtDue  = addMonths(parse(SAMPLE.fyEnd)!, rules.ctMonths);

  const LAYOUT = {
    section: 'Services',
    title: 'Compliance Calendar',
    icon: CalendarDays,
    subtitle: 'The standard UAE schedule. Set it once here and every client dashboard follows it.',
  };

  if (loading) return <AdminLayout {...LAYOUT} loading loadingLabel="Loading compliance calendar…" />;

  return (
    <AdminLayout
      {...LAYOUT}
      spaced
      actions={<>
        {dirty && <span className="hint" style={{ fontWeight: 700, color: 'var(--brand-d)' }}>Unsaved changes</span>}
        {savedAt && !dirty && <span className="ax-saved"><CheckCircle2 /> Saved {savedAt}</span>}
        {dirty && <button onClick={discard} className="btn">Discard</button>}
        <button onClick={handleSave} disabled={saving || !dirty} className="btn btn-pri">
          <Save />{saving ? 'Saving…' : 'Save & push to clients'}
        </button>
      </>}
    >
      {loadError && <div className="errbar">{loadError}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-4 items-start">

        {/* ── LEFT: the three things there are to set ───────────────────── */}
        <div className="ax-stack">

          {/* 1 · Deadline rules, written as sentences */}
          <div className="card">
            <div className="card-h">
              <div>
                <b>1 · Deadline rules</b>
                <span className="block">Every client's dates are calculated from these four numbers.</span>
              </div>
            </div>
            <div className="card-b">
              <RuleRow example={`A quarter ending ${fmt(parse(SAMPLE.vatPeriodEnd))} would be due ${fmt(sampleVatDue)}.`}>
                The VAT return and payment are due
                <NumIn value={rules.vatDays} min={1} max={365} onChange={v => updateRule('vatDays', v)} />
                days after each VAT period ends.
              </RuleRow>

              <RuleRow example={`A year ending ${fmt(parse(SAMPLE.fyEnd))} would be due ${fmt(sampleCtDue)}.`}>
                The Corporate Tax return and payment are due
                <NumIn value={rules.ctMonths} min={1} max={24} width={58} onChange={v => updateRule('ctMonths', v)} />
                months after the financial year ends.
              </RuleRow>

              <RuleRow example={`Currently AED ${rules.vatThreshold.toLocaleString()} — the FTA's mandatory registration threshold.`}>
                VAT registration becomes mandatory at AED
                <NumIn value={rules.vatThreshold} min={0} max={10000000} width={110} onChange={v => updateRule('vatThreshold', v)} />
                of turnover.
              </RuleRow>

              <RuleRow example={`Next close falls on ${fmt(nextMonthDay(rules.bookDay))}.`}>
                Bookkeeping closes and documents are due on day
                <NumIn value={rules.bookDay} min={1} max={28} width={58} onChange={v => updateRule('bookDay', v)} />
                of the following month.
              </RuleRow>
            </div>
          </div>

          {/* 2 · Reminders, as a set of one-click days */}
          <div className="card">
            <div className="card-h">
              <div>
                <b>2 · Reminder emails</b>
                <span className="block">Click a day to switch its reminder on or off.</span>
              </div>
              <span className="hint">
                <BellRing style={{ width: 13, height: 13, display: 'inline', verticalAlign: '-2px', marginInlineEnd: 4 }} />
                {reminders.length} per deadline
              </span>
            </div>
            <div className="card-b">
              <div className="chips">
                {reminderChips.map(d => (
                  <button
                    key={d}
                    onClick={() => toggleReminder(d)}
                    className={`chip${reminders.includes(d) ? ' active' : ''}`}
                  >
                    {d === 1 ? '1 day' : `${d} days`}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-4">
                <input
                  type="number" min={1} max={365} value={customDay} placeholder="Other"
                  onChange={e => setCustomDay(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addCustomReminder(); }}
                  className="inp" style={{ width: 92 }}
                />
                <button onClick={addCustomReminder} className="btn"><Plus /> Add day</button>
              </div>
            </div>
            <div className="card-f">
              <span className="note">
                {reminders.length === 0
                  ? 'No reminders — clients get no warning before a deadline.'
                  : `First warning ${Math.max(...reminders)} days out, last ${Math.min(...reminders) === 1 ? 'the day before' : `${Math.min(...reminders)} days before`}.`}
              </span>
            </div>
          </div>

          {/* 3 · Fixed dates */}
          <div className="card">
            <div className="card-h">
              <div>
                <b>3 · Fixed dates for everyone</b>
                <span className="block">One-off dates that are the same for every client, like an ESR deadline.</span>
              </div>
            </div>
            <div className="card-b">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_150px_170px_auto] gap-2 items-end">
                <div>
                  <label className="lbl">What is due</label>
                  <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                    placeholder="e.g. ESR notification deadline" className="inp"
                    onKeyDown={e => { if (e.key === 'Enter') addDate(); }} />
                </div>
                <div>
                  <label className="lbl">Category</label>
                  <input type="text" value={newCat} onChange={e => setNewCat(e.target.value)}
                    placeholder="ESR" className="inp" />
                </div>
                <div>
                  <label className="lbl">Date</label>
                  <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="inp" />
                </div>
                <button onClick={addDate} disabled={!newName.trim() || !newDate} className="btn btn-pri">
                  <Plus /> Add
                </button>
              </div>

              {datesByDay.length === 0 ? (
                <div className="empty" style={{ padding: '28px 20px' }}>
                  <CalendarDays />
                  <b>No fixed dates yet</b>
                  <p>Clients still get every deadline the rules above calculate.</p>
                </div>
              ) : (
                <div className="mt-4 space-y-2">
                  {datesByDay.map(o => (
                    <div key={o.i} className="flex items-center gap-3 border border-[var(--ax-line)] rounded-xl px-4 py-2.5">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-[var(--ax-ink)] truncate">{o.name}</div>
                        <div className="hint">{o.cat}</div>
                      </div>
                      <div className="text-right flex-none">
                        <div className="font-bold text-sm text-[var(--ax-ink)]">{fmt(parse(o.date))}</div>
                        <div className="hint">{relative(parse(o.date))}</div>
                      </div>
                      <button onClick={() => removeDate(o.i)} className="btn btn-ghost" style={{ padding: '8px 10px' }} aria-label={`Remove ${o.name}`}>
                        <Trash2 />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT: what a client ends up seeing ───────────────────────── */}
        <div className="xl:sticky xl:top-[88px]">
          <div className="card">
            <div className="card-h">
              <div>
                <b><Eye style={{ width: 15, height: 15, display: 'inline', verticalAlign: '-2px', marginInlineEnd: 6 }} />Client preview</b>
                <span className="block">A sample client, so you can see the rules land before saving.</span>
              </div>
            </div>
            <div className="card-b" style={{ paddingTop: 8, paddingBottom: 8 }}>
              <div className="divide-y divide-[var(--ax-line-2)]">
                {obligations.map((o, i) => {
                  const [statusLabel, statusKey] = statusOf(o.due);
                  const s = STATUS_STYLES[statusKey];
                  return (
                    <div key={i} className="flex items-center gap-3 py-3">
                      <span className={`w-2.5 h-2.5 rounded-full flex-none ${s.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-[var(--ax-ink)] truncate">
                          {o.n}
                          {o.std && <span className="text-[var(--brand-d)] font-semibold ml-1.5 text-xs">· fixed</span>}
                        </div>
                        <div className="hint">{o.cat} · {o.freq}</div>
                      </div>
                      <div className="text-right flex-none">
                        <div className="font-bold text-sm text-[var(--ax-ink)]">{fmt(o.due)}</div>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full inline-block mt-0.5 ${s.pill}`}>
                          {statusLabel}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="card-f">
              <span className="note">Saving replaces this schedule on every client dashboard.</span>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
